/** MIDI mapping system — Ableton-style "learn" mode for binding CC to parameters.
 *
 * Allows users to click a "Map" button, then click any UI control,
 * then move a MIDI controller knob/slider to create a persistent binding.
 */

import { DEFAULT_CC_MAP, type MidiCCMessage } from "./midiInput.ts";

/** A parameter target that can be MIDI-mapped. */
export type MappableParam =
	| "masterGain"
	| "filter.cutoff"
	| "filter.resonance"
	| "adsr.attack"
	| "adsr.decay"
	| "adsr.sustain"
	| "adsr.release";

/** All mappable parameter IDs. */
export const MAPPABLE_PARAMS: readonly MappableParam[] = [
	"masterGain",
	"filter.cutoff",
	"filter.resonance",
	"adsr.attack",
	"adsr.decay",
	"adsr.sustain",
	"adsr.release",
] as const;

/** Human-readable labels for each mappable parameter. */
export const PARAM_LABELS: Readonly<Record<MappableParam, string>> = {
	masterGain: "Volume",
	"filter.cutoff": "Filter Cutoff",
	"filter.resonance": "Resonance",
	"adsr.attack": "Attack",
	"adsr.decay": "Decay",
	"adsr.sustain": "Sustain",
	"adsr.release": "Release",
} as const;

/** A persisted mapping from a MIDI CC to a synth parameter. */
export type MidiMapping = {
	readonly cc: number;
	readonly channel: number;
	readonly param: MappableParam;
};

/** The state of the MIDI mapping system. */
export type MidiMappingState = {
	readonly isLearning: boolean;
	readonly learningParam: MappableParam | null;
	readonly mappings: readonly MidiMapping[];
	/** Last CC activity for visual feedback — { cc, value, timestamp }. */
	readonly lastActivity: MidiActivity | null;
};

/** Recent MIDI activity for visual feedback. */
export type MidiActivity = {
	readonly cc: number;
	readonly value: number;
	readonly timestamp: number;
};

/** Create the initial mapping state with default CC assignments. */
export function createInitialMappingState(): MidiMappingState {
	const mappings: MidiMapping[] = [];
	for (const [ccStr, param] of Object.entries(DEFAULT_CC_MAP)) {
		mappings.push({
			cc: Number(ccStr),
			channel: -1, // -1 = any channel
			param: param as MappableParam,
		});
	}
	return {
		isLearning: false,
		learningParam: null,
		mappings,
		lastActivity: null,
	};
}

/** Enter MIDI learn mode for a specific parameter. */
export function startLearning(
	state: MidiMappingState,
	param: MappableParam,
): MidiMappingState {
	return {
		...state,
		isLearning: true,
		learningParam: param,
	};
}

/** Exit MIDI learn mode without creating a mapping. */
export function cancelLearning(state: MidiMappingState): MidiMappingState {
	return {
		...state,
		isLearning: false,
		learningParam: null,
	};
}

/** Process a CC message during learn mode — creates or updates a binding. */
export function learnCC(
	state: MidiMappingState,
	msg: MidiCCMessage,
): MidiMappingState {
	if (!state.isLearning || !state.learningParam) return state;

	// Remove any existing mapping for this param
	const filtered = state.mappings.filter(
		(m) => m.param !== state.learningParam,
	);

	// Also remove any existing mapping for this CC (one CC → one param)
	const withoutCC = filtered.filter((m) => m.cc !== msg.cc);

	const newMapping: MidiMapping = {
		cc: msg.cc,
		channel: msg.channel,
		param: state.learningParam,
	};

	return {
		...state,
		isLearning: false,
		learningParam: null,
		mappings: [...withoutCC, newMapping],
	};
}

/** Remove a mapping for a specific parameter. */
export function removeMapping(
	state: MidiMappingState,
	param: MappableParam,
): MidiMappingState {
	return {
		...state,
		mappings: state.mappings.filter((m) => m.param !== param),
	};
}

/** Look up which parameter a CC is mapped to. */
export function resolveCC(
	state: MidiMappingState,
	msg: MidiCCMessage,
): MappableParam | undefined {
	const mapping = state.mappings.find(
		(m) => m.cc === msg.cc && (m.channel === -1 || m.channel === msg.channel),
	);
	return mapping?.param;
}

/** Update the last activity marker. */
export function updateActivity(
	state: MidiMappingState,
	msg: MidiCCMessage,
): MidiMappingState {
	return {
		...state,
		lastActivity: {
			cc: msg.cc,
			value: msg.value,
			timestamp: Date.now(),
		},
	};
}
